import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { money } from "../../lib/api";

const STATUSES = ["processing", "confirmed", "shipped", "delivered", "cancelled"];
const PAY_COLOR = { paid: "text-emerald-400", pending: "text-amber-300", failed: "text-[#58111A]", expired: "text-[#FAFAF7]/40" };

function OrderDetails({ o }) {
  const a = o.shipping_address || {};
  return (
    <tr data-testid={`admin-order-details-${o.id}`} className="bg-[#0B0B0C]/60 border-b border-[rgba(212,175,55,0.08)]">
      <td colSpan={6} className="px-6 py-4">
        <div className="grid sm:grid-cols-2 gap-6 text-xs">
          <div>
            <p className="eyebrow text-[0.55rem] mb-2">Items</p>
            {(o.items || []).map((it, i) => (
              <div key={i} className="flex justify-between text-[#FAFAF7]/80 py-1">
                <span>{it.name}{it.volume ? ` · ${it.volume}` : ""}{it.engraving ? ` · "${it.engraving}"` : ""} ×{it.quantity}</span>
                <span className="text-[#D4AF37]">{money(it.price * it.quantity)}</span>
              </div>
            ))}
            <div className="flex justify-between text-[#FAFAF7]/50 pt-2 border-t border-[rgba(212,175,55,0.12)] mt-2">
              <span>Shipping {o.express_fee ? "+ Express" : ""}</span><span>{money((o.shipping || 0) + (o.express_fee || 0))}</span>
            </div>
          </div>
          <div>
            <p className="eyebrow text-[0.55rem] mb-2">Ship to</p>
            <p className="text-[#FAFAF7]">{a.full_name}</p>
            <p className="text-[#FAFAF7]/70">{a.address}</p>
            <p className="text-[#FAFAF7]/70">{[a.city, a.zip, a.country].filter(Boolean).join(", ")}</p>
            <p className="text-[#FAFAF7]/70">{a.phone}</p>
            <p className="text-[#FAFAF7]/50 mt-2">{o.delivery_method === "express" ? "Express delivery" : "Standard delivery"}</p>
            {o.gift_message && <p className="text-[#D4AF37]/80 italic mt-2">Gift: "{o.gift_message}"</p>}
            <p className="text-[#FAFAF7]/40 mt-2">{new Date(o.created_at).toLocaleString("en-US")}</p>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function OrdersTable({ orders, onStatus }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="gold-border rounded-xl bg-[#141416] overflow-hidden overflow-x-auto" data-testid="admin-orders">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[rgba(212,175,55,0.18)] text-left">
            <th className="px-4 py-3 eyebrow text-[0.55rem]">Order</th>
            <th className="px-4 py-3 eyebrow text-[0.55rem]">Customer</th>
            <th className="px-4 py-3 eyebrow text-[0.55rem]">Total</th>
            <th className="px-4 py-3 eyebrow text-[0.55rem]">Payment</th>
            <th className="px-4 py-3 eyebrow text-[0.55rem]">Status</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#FAFAF7]/40">No orders yet</td></tr>}
          {orders.map((o) => (
            <React.Fragment key={o.id}>
              <tr data-testid={`admin-order-row-${o.id}`} className="border-b border-[rgba(212,175,55,0.08)]">
                <td className="px-4 py-3 text-[#FAFAF7]/70 font-mono-lux text-xs">#{o.id.slice(-8)}</td>
                <td className="px-4 py-3 text-[#FAFAF7]/70">{o.customer_email || "Guest"}</td>
                <td className="px-4 py-3 text-[#D4AF37]">{money(o.total)}</td>
                <td className="px-4 py-3"><span className={PAY_COLOR[o.payment_status] || "text-[#FAFAF7]/50"}>{o.payment_status}</span></td>
                <td className="px-4 py-3">
                  <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={(e) => onStatus(o.id, e.target.value)}
                    className="bg-[#0B0B0C] gold-border rounded px-2 py-1 text-xs text-[#FAFAF7] outline-none">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`admin-order-toggle-${o.id}`} onClick={() => setOpen(open === o.id ? null : o.id)} className="text-[#FAFAF7]/60 hover:text-[#D4AF37]">
                    {open === o.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </td>
              </tr>
              {open === o.id && <OrderDetails o={o} />}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
